import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Target, TrendingUp, Award, BookOpen, Zap, Shield, Search, Users, BarChart3, IndianRupee } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <SEOHead path="/" />
      {/* Sticky Nav */}
      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 lg:px-12 py-4 border-b border-earth-200">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-earth-700">O4S.ai</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="nav-skill2income-btn"
              onClick={() => navigate('/skill2income')}
              variant="ghost"
              className="font-semibold text-earth-600 hover:text-primary hover:bg-surface-alt btn-press hidden sm:inline-flex"
            >
              <IndianRupee className="w-4 h-4 mr-1" /> Skill2Income
            </Button>
            <Button
              data-testid="nav-browse-faqs-btn"
              onClick={() => navigate('/faqs')}
              variant="ghost"
              className="font-semibold text-earth-600 hover:text-primary hover:bg-surface-alt btn-press"
            >
              Ask O4S.AI
            </Button>
            <Button
              data-testid="nav-get-started-btn"
              onClick={() => navigate('/auth')}
              className="bg-primary hover:bg-primary-hover text-white font-semibold h-11 px-7 rounded-xl btn-press transition-colors"
            >
              Get Started <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="px-4 md:px-8 lg:px-12 py-16 md:py-24 animate-fade-in-up">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-7 space-y-7">
              <div className="inline-flex items-center gap-2 bg-surface-alt px-4 py-2 rounded-full border border-earth-200">
                <span className="w-2 h-2 rounded-full bg-success animate-pulse"></span>
                <span className="text-sm font-semibold text-earth-600">1,890+ Expert-Verified Career Answers</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-earth-700">
                Find Your
                <span className="block gradient-text">Perfect Career Path</span>
              </h2>
              <p className="text-lg leading-relaxed text-earth-600 max-w-lg">
                AI-powered career guidance built for Indian students. From 10th grade to Master's — discover paths based on your interests, skills, and goals.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  data-testid="hero-get-started-btn"
                  onClick={() => navigate('/auth')}
                  className="bg-primary hover:bg-primary-hover text-white h-14 px-10 text-base font-semibold rounded-xl btn-press transition-colors"
                >
                  Start Your Journey <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  data-testid="hero-browse-btn"
                  onClick={() => navigate('/faqs')}
                  className="h-14 px-8 text-base font-semibold rounded-xl border-2 border-earth-300 text-earth-700 bg-white hover:bg-secondary hover:text-white hover:border-secondary btn-press transition-all"
                >
                  <Search className="mr-2 w-5 h-5" /> Browse Careers
                </Button>
              </div>
              <div className="flex items-center gap-8 pt-2">
                <div>
                  <div className="text-3xl font-bold text-primary">106</div>
                  <div className="text-sm text-earth-500">Career Roadmaps</div>
                </div>
                <div className="w-px h-10 bg-earth-200"></div>
                <div>
                  <div className="text-3xl font-bold text-secondary">11</div>
                  <div className="text-sm text-earth-500">Industries</div>
                </div>
                <div className="w-px h-10 bg-earth-200"></div>
                <div>
                  <div className="text-3xl font-bold text-accent">1,890+</div>
                  <div className="text-sm text-earth-500">Expert FAQs</div>
                </div>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-3xl blur-xl"></div>
                <img
                  src="https://images.pexels.com/photos/7616700/pexels-photo-7616700.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Indian college students studying together"
                  className="relative rounded-2xl shadow-xl object-cover w-full aspect-[4/5]"
                />
                {/* Floating stat card */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg border border-earth-200 p-4 animate-fade-in" style={{animationDelay: '0.3s'}}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-success" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-earth-700">AP & Telangana Focused</div>
                      <div className="text-xs text-earth-500">Local salary data & insights</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 md:px-8 lg:px-12 py-16 md:py-24 bg-surface-alt">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-3">Why O4S.ai</p>
            <h3 className="text-3xl sm:text-4xl font-bold text-earth-700 mb-3">Career Guidance That Actually Works</h3>
            <p className="text-base text-earth-600 max-w-2xl mx-auto">Built specifically for Indian students with real salary data, local insights, and expert-verified information</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'AI Career Assessment', description: 'Get 5-6 personalized career paths in minutes based on your education, interests, and goals', color: 'bg-primary/10 text-primary' },
              { icon: BarChart3, title: 'Real Salary Data', description: 'Verified salary ranges from Naukri, AmbitionBox, Glassdoor — not guesswork', color: 'bg-success/10 text-success' },
              { icon: BookOpen, title: '106 Career Roadmaps', description: 'Step-by-step learning paths from 10th pass to career switcher for every role', color: 'bg-secondary/10 text-secondary' },
              { icon: Shield, title: 'AP & TG Focused', description: 'Local insights — Hyderabad, Vizag, Vijayawada job markets, colleges, and opportunities', color: 'bg-warning/10 text-warning' },
              { icon: Users, title: '1,890+ Expert FAQs', description: 'Comprehensive Q&A covering every education stage and career doubt', color: 'bg-primary/10 text-primary' },
              { icon: Award, title: 'Work Duties Included', description: 'Know exactly what you\'ll do on the job — daily tasks, not just job titles', color: 'bg-accent/10 text-accent' },
            ].map((feature, idx) => (
              <div key={idx} className="bg-white border border-earth-200 rounded-2xl p-7 card-hover" style={{animationDelay: `${idx * 0.08}s`}}>
                <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mb-4`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h4 className="text-lg font-bold text-earth-700 mb-2">{feature.title}</h4>
                <p className="text-earth-600 leading-relaxed text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-secondary mb-3">Simple Process</p>
            <h3 className="text-3xl sm:text-4xl font-bold text-earth-700">Get Your Career Roadmap in 5 Steps</h3>
          </div>
          <div className="space-y-6">
            {[
              { step: 1, title: 'Choose Your Education Level', desc: '10th, 12th, Degree, Post-grad, or Career Switcher' },
              { step: 2, title: 'Select Your Board & Stream', desc: 'CBSE, State Board, MPC, BiPC, MEC, CEC — we cover all' },
              { step: 3, title: 'Pick Your Subjects & Skills', desc: 'Tell us what you\'re good at and what excites you' },
              { step: 4, title: 'Share Your Goals', desc: 'Salary expectations, preferred location, work style' },
              { step: 5, title: 'Get AI-Powered Results', desc: '5-6 personalized career paths with full roadmaps and salary data' }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-5 items-start animate-fade-in-up" style={{animationDelay: `${idx * 0.1}s`}}>
                <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center text-lg font-bold flex-shrink-0 shadow-md">
                  {item.step}
                </div>
                <div className="flex-1 bg-white border border-earth-200 rounded-xl p-5 card-hover">
                  <h4 className="text-lg font-bold text-earth-700 mb-1">{item.title}</h4>
                  <p className="text-earth-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button
              data-testid="how-it-works-start-btn"
              onClick={() => navigate('/auth')}
              className="bg-primary hover:bg-primary-hover text-white h-14 px-10 text-base font-semibold rounded-xl btn-press transition-colors"
            >
              Start Assessment Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Skill2Income Banner */}
      <section className="px-4 md:px-8 lg:px-12 py-16 md:py-20 relative overflow-hidden" style={{ background: '#1B2A1F' }}>
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(ellipse at 10% 80%, rgba(217,90,65,0.15) 0%, transparent 50%), radial-gradient(ellipse at 90% 20%, rgba(45,106,79,0.2) 0%, transparent 50%)' }}></div>
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary px-4 py-2 rounded-full mb-5">
                <IndianRupee className="w-4 h-4 text-white" />
                <span className="text-sm font-bold text-white">New Feature</span>
              </div>
              <h3 className="text-3xl sm:text-4xl font-bold text-white mb-4">Skill2Income</h3>
              <p className="text-lg text-white/90 mb-7 leading-relaxed">Know exactly which skills to learn to earn your target income. Get a personalized roadmap from your current level to your dream salary.</p>
              <Button
                data-testid="s2i-banner-btn"
                onClick={() => navigate('/skill2income')}
                className="bg-primary hover:bg-primary-hover text-white h-14 px-8 text-base font-semibold rounded-xl btn-press"
              >
                Check Your Earning Potential <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { val: '100+', label: 'Career Paths', accent: '#D95A41' },
                { val: '5 Steps', label: 'Quick Analysis', accent: '#E8A838' },
                { val: '90-Day', label: 'Action Plan', accent: '#2D6A4F' },
                { val: '3 Paths', label: 'Job + Freelance + Biz', accent: '#3B82F6' },
              ].map((s, i) => (
                <div key={i} className="rounded-xl p-5 shadow-lg" style={{ background: '#FFFFFF', borderLeft: `4px solid ${s.accent}` }}>
                  <p className="text-2xl font-bold" style={{ color: s.accent }}>{s.val}</p>
                  <p className="text-xs font-medium text-earth-600 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 md:px-8 lg:px-12 py-16 md:py-24 bg-secondary">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h3 className="text-3xl sm:text-4xl font-bold mb-5">Ready to Discover Your Future?</h3>
          <p className="text-lg mb-8 text-white/80 max-w-2xl mx-auto">Join thousands of AP & Telangana students who found clarity in their career journey</p>
          <Button
            data-testid="cta-section-start-btn"
            onClick={() => navigate('/auth')}
            className="bg-white text-secondary hover:bg-earth-100 h-14 px-10 text-base font-bold rounded-xl btn-press transition-colors"
          >
            Get Started Free <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 md:px-8 lg:px-12 py-10 bg-earth-700 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-2.5 mb-4">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold">O4S.ai</span>
          </div>
          <p className="text-earth-400 text-sm text-center mb-6">Empowering Indian students to make informed career decisions</p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm mb-6">
            <a href="/about" data-testid="footer-about-link" className="text-earth-400 hover:text-white transition-colors">About</a>
            <a href="/founder" data-testid="footer-founder-link" className="text-earth-400 hover:text-white transition-colors">Founder</a>
            <a href="/contact" data-testid="footer-contact-link" className="text-earth-400 hover:text-white transition-colors">Contact</a>
            <a href="/privacy" data-testid="footer-privacy-link" className="text-earth-400 hover:text-white transition-colors">Privacy Policy</a>
            <a href="/terms" data-testid="footer-terms-link" className="text-earth-400 hover:text-white transition-colors">Terms & Conditions</a>
            <a href="/legal" data-testid="footer-legal-link" className="text-earth-400 hover:text-white transition-colors">Disclaimer & Legal</a>
          </div>
          <p className="text-earth-500 text-xs text-center">&copy; 2026 O4S.ai. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
